import { useEffect } from "react";
import { useLocation } from "wouter";
import { showBackButton, hideBackButton, getTelegramWebApp } from "@/lib/telegram-service";
import { useApp } from "@/lib/context";

interface TelegramBackButtonProps {
  // Se specificato, torna a questo percorso invece che alla history precedente
  backTo?: string;
  // Handler personalizzato per il pulsante back (sovrascrive il comportamento di default)
  onBack?: () => void;
}

/**
 * Componente che gestisce il pulsante indietro di Telegram nelle pagine non presenti nella navbar.
 * Automaticamente visualizza/nasconde il pulsante indietro in base alla navigazione.
 * Quando viene cliccato, gestisce il ritorno alla pagina precedente o a un percorso specificato.
 * 
 * NOTA: Quando il profilo utente è aperto (isAnyProfileSheetOpen = true), questo componente
 * non fa nulla perché il profilo gestisce autonomamente il back button.
 */
export function TelegramBackButton({ backTo, onBack }: TelegramBackButtonProps) {
  const [location, navigate] = useLocation();
  const { isAnyProfileSheetOpen, isEventSheetOpen } = useApp();
  
  useEffect(() => {
    // Se il profilo utente o la scheda evento sono aperti, NON gestire il back button
    // perché sono loro a gestirlo autonomamente
    if (isAnyProfileSheetOpen || isEventSheetOpen) {
      console.log('[TelegramBackButton] Profile or Event sheet is open, skipping back button management');
      return;
    }
    
    // Pagine della navbar che NON devono avere il pulsante back
    const navbarPages = ["/", "/discover", "/home", "/events", "/groups", "/chat", "/speed", "/wall"];
    
    // Determina se siamo in una pagina che fa parte della navbar principale
    const isNavbarPage = navbarPages.includes(location);
    
    if (!isNavbarPage) {
      // Verifica se il BackButton è già gestito da altri componenti (come Header)
      const webApp = getTelegramWebApp();
      if (webApp && webApp.BackButton && webApp.BackButton.isVisible) {
        console.log('[TelegramBackButton] BackButton already managed by another component, skipping');
        return;
      }
      
      // Siamo in una pagina secondaria, mostra il pulsante indietro
      showBackButton(() => {
        if (onBack) {
          // Se è stato specificato un handler personalizzato, usa quello
          onBack();
        } else if (backTo) {
          // Se è stato specificato un percorso di ritorno, usa quello
          navigate(backTo);
        } else {
          // Altrimenti torna alla pagina discover (fallback sicuro)
          navigate("/discover");
        }
      });
      
      // Cleanup quando il componente viene smontato
      return () => {
        hideBackButton();
      };
    } else {
      // Siamo in una pagina della navbar, nascondi il pulsante indietro
      hideBackButton();
    }
  }, [location, navigate, backTo, onBack, isAnyProfileSheetOpen, isEventSheetOpen]);
  
  // Questo è un componente di utility, quindi non renderizza nulla
  return null;
}